import { getCurrentUser } from '@back-office/core';
import { http } from '../config';
import {
  APPROVE_RECURRING_FILE_PARTIAL_ENDPOINT,
  APPROVE_RECURRING_FILE_PENDING_ENDPOINT,
  CANCEL_REASON_TYPES_ENDPOINT,
  RECURRING_APPROVED_FILE_CHECKLIST,
  RECURRING_ENDPOINT,
  RECURRING_ENROLLMENT_REPORT_ENDPOINT,
  RECURRING_ENROLLMENT_TYPE_ENDPOINT,
  RECURRING_FILE_DOWNLOAD_ENDPOINT,
  RECURRING_FILE_SEARCH_ENDPOINT,
  RECURRING_FILE_UPLOAD_ENDPOINT,
  RECURRING_REQUEST_ENDPOINT,
  RECURRING_TRANSACTION_REPORT_ENDPOINT,
  RECURRING_TRANSACTION_SEARCH_ENDPOINT,
  REJECT_REASON_TYPES_ENDPOINT,
  REJECT_RECURRING_FILE_ENDPOINT,
  UPDATE_RECURRING_ENDPOINT,
} from '../endpoints';
import {
  CancelReasonTypesResponse,
  DownloadFileRecurringResponse,
  RecurringEnrollmentReportResponse,
  RecurringEnrollmentTypeResponse,
  RecurringRequestListResponse,
  RecurringResponse,
  RecurringTransactionReportResponse,
  RecurringTransactionSearchResponse,
  RejectReasonTypesResponse,
  SearchRecurringResponse,
  SuccessResponse,
} from '../types';

export async function getRecurringsRequestList(
  query: string
): Promise<RecurringRequestListResponse> {
  return http.get(`${RECURRING_REQUEST_ENDPOINT}?${query}`);
}

export async function getRecurringEnrollmentReport(
  query: string
): Promise<RecurringEnrollmentReportResponse> {
  return http.get(`${RECURRING_ENROLLMENT_REPORT_ENDPOINT}?${query}`);
}

export async function getSearchRecurring(
  query: string
): Promise<SearchRecurringResponse> {
  return http.get(`${RECURRING_FILE_SEARCH_ENDPOINT}?${query}`);
}

export async function uploadFileRecurring(
  file: FormData,
  merchantID: string
): Promise<RecurringEnrollmentReportResponse> {
  return http.post(
    `${RECURRING_FILE_UPLOAD_ENDPOINT}?merchantID=${merchantID}`,
    file,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
}

export async function getRecurring(
  recurringId: string
): Promise<RecurringResponse> {
  const currentUser = getCurrentUser();
  return http.get(`${RECURRING_ENDPOINT}/${recurringId}`, {
    params: { merchantID: currentUser.merchantID },
  });
}

export async function updateRecurring(
  recurringId: string,
  recurringStatus: string,
  reason: string
): Promise<SuccessResponse> {
  const currentUser = getCurrentUser();
  return http.post(
    UPDATE_RECURRING_ENDPOINT,
    { id: recurringId, status: recurringStatus, reason },
    {
      params: { merchantID: currentUser.merchantID },
    }
  );
}

export async function getRejectReasonTypes(): Promise<RejectReasonTypesResponse> {
  const currentUser = getCurrentUser();
  return http.get(`${REJECT_REASON_TYPES_ENDPOINT}`, {
    params: { merchantID: currentUser.merchantID },
  });
}

export async function getCancelReasonTypes(): Promise<CancelReasonTypesResponse> {
  const currentUser = getCurrentUser();
  return http.get(`${CANCEL_REASON_TYPES_ENDPOINT}`, {
    params: { merchantID: currentUser.merchantID },
  });
}

export async function approveRecurringFilePendingToPartial(
  id: string[],
  requestedAt: string
): Promise<SuccessResponse> {
  const currentUser = getCurrentUser();
  return http.post(
    APPROVE_RECURRING_FILE_PENDING_ENDPOINT,
    { id, requested_at: requestedAt },
    {
      params: { merchantID: currentUser.merchantID },
    }
  );
}

export async function approveRecurringFilePartialToInQueue(
  id: string[],
  requestedAt: string
): Promise<SuccessResponse> {
  const currentUser = getCurrentUser();
  return http.post(
    APPROVE_RECURRING_FILE_PARTIAL_ENDPOINT,
    { id, requested_at: requestedAt },
    {
      params: { merchantID: currentUser.merchantID },
    }
  );
}

export async function getRecurringTransactionSearch(
  query: string
): Promise<RecurringTransactionSearchResponse> {
  return http.get(`${RECURRING_TRANSACTION_SEARCH_ENDPOINT}?${query}`);
}

export async function rejectRecurringFile(
  id: string[],
  reason: string | undefined
): Promise<SuccessResponse> {
  const currentUser = getCurrentUser();

  return http.post(
    REJECT_RECURRING_FILE_ENDPOINT,
    { id, reject_reason: reason },
    {
      params: { merchantID: currentUser.merchantID },
    }
  );
}

export async function getRecurringTransactionReport(
  query: string
): Promise<RecurringTransactionReportResponse> {
  return http.get(`${RECURRING_TRANSACTION_REPORT_ENDPOINT}?${query}`);
}

export async function getFileRecurring(
  fileName: string
): Promise<DownloadFileRecurringResponse> {
  const currentUser = getCurrentUser();
  return http.get(RECURRING_FILE_DOWNLOAD_ENDPOINT, {
    params: { merchantID: currentUser.merchantID, file_name: fileName },
  });
}

export async function checkListRecurringApproved(data: {
  onboardBatchID: string[];
}): Promise<SuccessResponse> {
  const currentUser = getCurrentUser();
  return http.post(RECURRING_APPROVED_FILE_CHECKLIST, data, {
    params: { merchantID: currentUser.merchantID },
  });
}

export async function getRecurringsEnrollmentType(): Promise<RecurringEnrollmentTypeResponse> {
  const currentUser = getCurrentUser();
  return http.get(
    `${RECURRING_ENROLLMENT_TYPE_ENDPOINT}?feature=BillerEnrollment&merchantID=${currentUser.merchantID}`
  );
}
